import styled from '@emotion/styled';
import { oneOfType, oneOf, bool, number, string, array } from 'prop-types';
import { variant } from 'styled-system';
import Box from './layouts/Box';
import Flex from './layouts/Flex';
import Stack from './layouts/Stack';
import theme from '../../theme';
import { currencyFormatter, percentageFormatter } from '@dnhb/formatters';
import { useMediaQuery } from '@dnhb/node_package';
import { useTheme } from '@emotion/react';
import {useTranslation} from "react-i18next";

const Label = styled(Box)([
  { overflowWrap: 'anywhere', hyphens: 'auto' },
  variant({
    variants: {
      active: {
        color: 'primary',
      },
      inactive: {
        color: 'primaryFont',
      },
      succes: {
        color: 'primary',
      },
    },
  }),
]);

Label.defaultProps = {
  as: 'label',
};

const DisplayBox = styled(Box)([
  {
    borderRadius: 4,
    padding: '8px 8px',
    border: '1px solid',
    minHeight: 'calc(1.5rem + 2 * 8px)',
  },
  variant({
    variants: {
      active: {
        borderColor: 'primary',
        bg: 'primaryActive',
      },
      inactive: {
        borderColor: 'background',
        bg: 'background',
      },
      succes: {
        borderColor: 'succes',
        bg: 'succesBackground',
      },
    },
  }),
]);

DisplayBox.defaultProps = {
  color: 'primaryFont',
};

const DisplayField = ({
  value,
  variant,
  isCurrency,
  isPercentage,
  ...props
}) => {
  const { t } = useTranslation();
  let v = value;
  if (typeof value === 'boolean') {
    v = value ? t('Ja') : t('Nee');
  } else if (isCurrency) {
    v = currencyFormatter(Number(value));
  } else if (isPercentage) {
    v = percentageFormatter(Number(value));
  }

  return (
    <DisplayBox variant={variant} flexGrow={1} {...props}>
      {v}
    </DisplayBox>
  );
};

const Display = ({
  label,
  variant,
  space,
  value,
  isCurrency,
  isPercentage,
}) => {
  const { breakpoints } = useTheme();
  const isMinDesktop = useMediaQuery(
    `(min-width: ${breakpoints.ml ?? '56.25rem'})`
  );

  return (
    <Stack space={space}>
      {label.length > 0 && <Label variant={variant}>{label}</Label>}
      {Array.isArray(value) ? (
        <Flex
          flexDirection={{ mobile: 'column', desktop: 'row' }}
          css={{
            '> * + *': {
              margin: isMinDesktop ? '0 0 0 12px' : '12px 0 0 0',
            },
          }}
        >
          {value.map((v, i) => (
            <DisplayField
              variant={variant}
              value={v}
              key={i}
              isCurrency={isCurrency}
              isPercentage={isPercentage}
            />
          ))}
        </Flex>
      ) : (
        <DisplayField
          value={value}
          variant={variant}
          isCurrency={isCurrency}
          isPercentage={isPercentage}
        />
      )}
    </Stack>
  );
};

Display.propTypes = {
  value: oneOfType([bool, number, string, array]),
  label: string,
  variant: oneOf(['active', 'inactive', 'succes']),
  space: oneOf(Object.keys(theme.space)),
  isCurrency: bool,
  isPercentage: bool,
};

Display.defaultProps = {
  variant: 'inactive',
  label: '',
  space: 's',
  isCurrency: false,
  isPercentage: false,
};

export default Display;
